import React, { useState, useEffect, useRef } from 'react';
import { Star } from 'react-iconly';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { fetchTestimonials } from '../services/supabaseService';
import { useLanguage } from '../context/LanguageContext';
import './Testimonials.css';

gsap.registerPlugin(ScrollTrigger);

const CARD_ROTATIONS = [4, -5, 5, -8, 5];

const CARD_COLORS = [
  { bg: '#29725f', text: '#ffffff' }, // Green
  { bg: '#82a0ff', text: '#1a1a1a' }, // Light Blue
  { bg: '#f5693c', text: '#1a1a1a' }, // Orange
  { bg: '#a0325a', text: '#ffffff' }, // Maroon
  { bg: '#f0befa', text: '#1a1a1a' }, // Pink
];

const Testimonials = () => {
  const { t } = useLanguage();
  const [testimonials, setTestimonials] = useState([]);
  const cardsRef = useRef([]);
  const wrapperRef = useRef(null);
  const cleanupFnsRef = useRef([]);
  const localScrollTriggersRef = useRef([]);
  const initializedRef = useRef(false);

  useEffect(() => {
    const loadTestimonials = async () => {
      const data = await fetchTestimonials();
      setTestimonials(data);
    };
    loadTestimonials();
  }, []);

  useEffect(() => {
    if (!testimonials.length) return;

    cleanupFnsRef.current.forEach(fn => fn());
    cleanupFnsRef.current = [];
    localScrollTriggersRef.current.forEach(st => st.kill());
    localScrollTriggersRef.current = [];

    const underlineTl = gsap.to('.testimonials-underline-svg path', {
      strokeDashoffset: 0,
      duration: 1.2,
      ease: 'power3.out',
      stagger: 0.3,
      scrollTrigger: {
        trigger: '.testimonials-wrapper',
        start: 'top 70%',
        toggleActions: 'play none none reverse'
      }
    });

    const createdTriggers = underlineTl.scrollTrigger ? [underlineTl.scrollTrigger] : [];
    localScrollTriggersRef.current.push(...createdTriggers);

    initCardAnimations();

    return () => {
      cleanupFnsRef.current.forEach(fn => fn());
      cleanupFnsRef.current = [];
      localScrollTriggersRef.current.forEach(st => st.kill());
      localScrollTriggersRef.current = [];
    };
  }, [testimonials]);

  function initCardAnimations() {
    const cards = cardsRef.current.filter(Boolean);
    if (!cards.length) return;

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    let leaveTimeout = null;

    if (!isMobile) {
      const cleanups = [];

      cards.forEach((card, index) => {
        const handleMouseEnter = () => {
          if (leaveTimeout) { clearTimeout(leaveTimeout); leaveTimeout = null; }
          const hoverGap = 40;
          const clusterGap = 90;
          const cardWidth = 320;
          const hoveredLeft = cards[index].offsetLeft;
          const leftCards = [];
          const rightCards = [];

          cards.forEach((otherCard, otherIndex) => {
            if (otherIndex < index) leftCards.push({ card: otherCard, index: otherIndex });
            else if (otherIndex > index) rightCards.push({ card: otherCard, index: otherIndex });
          });

          const currentTop = cards[index].offsetTop;
          const targetCommonTop = 50;
          const moveY = targetCommonTop - currentTop;

          gsap.to(cards[index], { x: 0, y: moveY, rotation: 0, scale: 1.08, duration: 0.9, ease: 'elastic.out(1, 0.5)', overwrite: true });

          if (rightCards.length) {
            const clusterStart = hoveredLeft + cardWidth + hoverGap;
            rightCards.forEach((item, i) => {
              const targetAbsLeft = clusterStart + (i * clusterGap);
              const targetX = Math.max(targetAbsLeft - item.card.offsetLeft, 10);
              const angleRad = CARD_ROTATIONS[item.index] * (Math.PI / 180);
              const targetY = targetX * Math.tan(angleRad);
              gsap.to(item.card, { x: targetX, y: targetY, rotation: CARD_ROTATIONS[item.index], scale: 1, duration: 1.0, ease: 'elastic.out(1, 0.5)', overwrite: true });
            });
          }

          if (leftCards.length) {
            leftCards.reverse();
            const clusterStart = hoveredLeft - hoverGap - cardWidth;
            leftCards.forEach((item, i) => {
              const targetAbsLeft = clusterStart - (i * clusterGap);
              const targetX = Math.min(targetAbsLeft - item.card.offsetLeft, -10);
              const angleRad = CARD_ROTATIONS[item.index] * (Math.PI / 180);
              const targetY = targetX * Math.tan(angleRad);
              gsap.to(item.card, { x: targetX, y: targetY, rotation: CARD_ROTATIONS[item.index], scale: 1, duration: 1.0, ease: 'elastic.out(1, 0.5)', overwrite: true });
            });
          }
        };

        const handleMouseLeave = () => {
          leaveTimeout = setTimeout(() => {
            cards.forEach((c, i) => {
              gsap.to(c, { x: 0, y: 0, scale: 1, rotation: CARD_ROTATIONS[i], duration: 1.0, ease: 'elastic.out(1, 0.5)', overwrite: true, zIndex: i + 1 });
            });
          }, 80);
        };

        card.addEventListener('mouseenter', handleMouseEnter);
        card.addEventListener('mouseleave', handleMouseLeave);

        cleanups.push(() => {
          card.removeEventListener('mouseenter', handleMouseEnter);
          card.removeEventListener('mouseleave', handleMouseLeave);
        });
      });

      cleanups.push(() => {
        if (leaveTimeout) clearTimeout(leaveTimeout);
      });

      cleanupFnsRef.current.push(...cleanups);
    } else {
      // Mobile: Stacked card scroll reveal
      const scrollPerCard = window.innerHeight * 0.8;
      const navH = 60;
      const mobileRotations = [-6, 4, -8, 5, -3];

      cards.forEach((card, i) => {
        gsap.set(card, {
          position: 'absolute', left: '50%', top: '0', xPercent: -50,
          y: i === 0 ? 0 : window.innerHeight * 1.1,
          rotation: mobileRotations[i % mobileRotations.length],
          zIndex: i + 1,
          transformOrigin: 'center center'
        });
      });

      const wrapperH = window.innerHeight * 0.7 + scrollPerCard * (cards.length - 1);
      gsap.set(wrapperRef.current, { height: wrapperH });

      const pinTrigger = ScrollTrigger.create({
        trigger: wrapperRef.current,
        start: `top ${navH}px`,
        end: `+=${scrollPerCard * (cards.length - 1)}`,
        pin: true,
        pinSpacing: true,
        id: 'mobile-testimonials-pin'
      });
      localScrollTriggersRef.current.push(pinTrigger);

      cards.forEach((card, i) => {
        if (i === 0) return;
        const st = gsap.fromTo(card,
          { y: window.innerHeight * 1.1 },
          {
            y: 0,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: wrapperRef.current,
              start: `top+=${(i - 1) * scrollPerCard} ${navH}px`,
              end: `top+=${i * scrollPerCard} ${navH}px`,
              scrub: 0.4
            }
          }
        );
        if (st.scrollTrigger) localScrollTriggersRef.current.push(st.scrollTrigger);
      });
    }
  }

  return (
    <section className="testimonials-section">
      {/* Heading with SVG underline */}
      <div className="testimonials-title-container">
        <h2 className="testimonials-main-title">
          {t('contact.testimonialsTitle')} <span className="testimonials-italic-text">:</span>
        </h2>
        <svg xmlns="http://www.w3.org/2000/svg" width="160" viewBox="0 0 159 17" fill="none" className="testimonials-underline-svg">
          <path d="M1 12.1515C53.0771 5.7187 105.529 2.30552 158 1.93652" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
          <path d="M30.2672 15.9461C64.1899 12.8158 98.2663 11.3583 132.33 11.5735" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
        </svg>
      </div>

      {/* Testimonial Cards */}
      <div className="testimonials-cards-wrapper" ref={wrapperRef}>
        {testimonials.map((t, idx) => (
          <div
            key={t.id || idx}
            className={`testimonials-card card-${idx + 1}`}
            style={{
              backgroundColor: t.color || CARD_COLORS[idx % CARD_COLORS.length].bg,
              color: t.textColor || CARD_COLORS[idx % CARD_COLORS.length].text,
              transform: `rotate(${CARD_ROTATIONS[idx % CARD_ROTATIONS.length]}deg)`,
              zIndex: idx + 1
            }}
            ref={el => cardsRef.current[idx] = el}
          >
            {/* Author name as title */}
            <h3 className="testimonials-card-title">{t.author}</h3>

            {/* Dashed divider */}
            <svg width="100%" height="10" className="testimonials-card-divider" aria-hidden="true">
              <line x1="0" y1="5" x2="100%" y2="5" stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" opacity="0.4" />
            </svg>

            {/* Testimonial text as list */}
            <p className="testimonials-card-text">{t.text}</p>

            {/* Role */}
            <div className="testimonials-card-role">{t.role}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Testimonials;
